import { useEffect, useRef, useState } from 'react';
import { cx } from '../cx';
import s from './ChaosField.module.css';

/* =========================================================================
   The chaos field.

   A slowly-evolving, domain-warped fractal noise field, knocked out by a word.
   The chaos lives INSIDE the letterforms — it is never a background wash.

   How it works, top to bottom:

   1. A real, selectable <span> ("the ghost") sets the box and carries the text
      for assistive technology and for anyone without canvas. Once the canvas
      has painted a frame the ghost turns transparent; if the canvas never
      paints, the word simply stays legible. Nothing is ever blank.

   2. The field itself is value-noise fBm, written here rather than pulled from
      a library. `fbm()` sums four octaves, rotating the lattice between each
      so the result has no axis-aligned grain. `field()` then domain-warps it:
      two fBm lookups displace the coordinates handed to a third. Time enters
      only as an offset inside the warp, which is why the pattern evolves
      without ever translating across the screen and never visibly loops —
      there is no period to come back to.

   3. Cost control. The noise is evaluated into a small offscreen buffer capped
      at ~12,000 pixels and then drawn up to full size with smoothing. Warped
      fBm is low-frequency, so the upscale costs nothing visually and turns a
      megapixel-per-frame problem into ~144k noise evaluations. The visible
      canvas is capped at 1.5× CSS pixels and the loop is throttled to 24fps.

   4. The knockout. The field is painted, then the word is drawn with
      `globalCompositeOperation = 'destination-in'`, which discards every pixel
      outside the letterforms. A flash-coloured copy is then dropped in behind,
      offset by two pixels, so the word carries a deliberate misregistration
      like a badly-lined-up print plate.

   5. It stops. An IntersectionObserver cancels the loop when the word leaves
      the viewport and a visibilitychange listener cancels it when the tab is
      hidden. `prefers-reduced-motion` paints exactly one frame — a fixed,
      handsome one — and never schedules another.
   ========================================================================= */

/** Roughly `motion.drift` (42s) expressed as noise-space units per second. */
const DRIFT_PER_SECOND = 0.062;
/** ~24fps. */
const FRAME_MS = 1000 / 24;
/** Noise samples per frame. The whole performance budget lives in this number. */
const MAX_FIELD_PIXELS = 12000;
const MAX_DPR = 1.5;
/** The single frame shown to anyone who has asked for reduced motion. */
const FROZEN_PHASE = 3.74;

/* ---------------------------------------------------------------- noise */

function hash(ix: number, iy: number): number {
  let h = Math.imul(ix, 374761393) + Math.imul(iy, 668265263);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/** Value noise with a smoothstep-interpolated integer lattice. */
function noise(x: number, y: number): number {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const fx = x - ix;
  const fy = y - iy;
  const ux = fx * fx * (3 - 2 * fx);
  const uy = fy * fy * (3 - 2 * fy);
  const a = hash(ix, iy);
  const b = hash(ix + 1, iy);
  const c = hash(ix, iy + 1);
  const d = hash(ix + 1, iy + 1);
  return a + (b - a) * ux + (c - a) * uy + (a - b - c + d) * ux * uy;
}

/** Four octaves, rotated between each so nothing lines up with the pixel grid. */
function fbm(x: number, y: number): number {
  let value = 0;
  let amplitude = 0.5;
  let px = x;
  let py = y;
  for (let octave = 0; octave < 4; octave += 1) {
    value += amplitude * noise(px, py);
    const nx = 1.97 * px + 1.31 * py;
    const ny = -1.31 * px + 1.97 * py;
    px = nx;
    py = ny;
    amplitude *= 0.5;
  }
  return value;
}

/**
 * Domain warp. `t` displaces the coordinates fed to the warp lookups rather
 * than the final sample, so the structure reorganises itself in place instead
 * of sliding past.
 */
function field(x: number, y: number, t: number): number {
  const q1 = fbm(x + 0.31, y + 0.17 + t);
  const q2 = fbm(x + 5.2, y + 1.3 - t * 0.61);
  return fbm(x + 3.6 * q1 + 1.7, y + 3.6 * q2 + 9.2);
}

/* ---------------------------------------------------------------- colour */

/** ink → oxblood → bone, with a thin flash contour where the field crosses. */
const RAMP: Array<[number, number, number, number]> = [
  [0.0, 0x12, 0x10, 0x11],
  [0.26, 0x4a, 0x12, 0x1f],
  [0.5, 0x7e, 0x24, 0x36],
  [0.7, 0xa8, 0x40, 0x4f],
  [0.88, 0xef, 0xeb, 0xe3],
  [1.0, 0xff, 0xff, 0xff],
];

/* ---------------------------------------------------------------- helpers */

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

interface Metrics {
  /** Text box relative to the canvas box. */
  left: number;
  centerY: number;
  width: number;
}

function measureGhost(ghost: HTMLElement, canvas: HTMLCanvasElement): Metrics | null {
  const node = ghost.firstChild;
  if (!node) return null;
  const range = document.createRange();
  range.selectNodeContents(ghost);
  const text = range.getBoundingClientRect();
  const box = canvas.getBoundingClientRect();
  if (text.width < 1 || box.width < 1) return null;
  return {
    left: text.left - box.left,
    centerY: (text.top + text.bottom) / 2 - box.top,
    width: text.width,
  };
}

interface FaceSpec {
  style: string;
  family: string;
  /** The size CSS is using, before any correction. */
  size: number;
  /** CSS tracking as a fraction of the em, so it can follow a size change. */
  trackingEm: number;
}

type SpacedContext = CanvasRenderingContext2D & { letterSpacing: string };

/**
 * Match the painted word to the real one.
 *
 * Canvas has to be told the tracking explicitly. Where `ctx.letterSpacing`
 * exists (Chrome, Safari) it is set proportionally to the size, which means
 * the whole advance scales linearly and one correction pass lands exactly on
 * the CSS width. Where it does not (Firefox) the size is solved for width
 * instead, so the word still occupies the right box — a couple of percent of
 * extra glyph width is invisible; a word that does not line up is not.
 *
 * Getting this wrong is very loud: an earlier pass omitted the tracking and
 * then scaled the size to compensate, which piled the letters on top of each
 * other on any headline with real negative tracking.
 */
function setFace(ctx: CanvasRenderingContext2D, face: FaceSpec, size: number): void {
  ctx.font = `${face.style} 400 ${size}px ${face.family}`;
  if ('letterSpacing' in ctx) {
    (ctx as SpacedContext).letterSpacing = `${face.trackingEm * size}px`;
  }
}

function fitFace(
  ctx: CanvasRenderingContext2D,
  text: string,
  face: FaceSpec,
  target: number,
  available: number,
): void {
  let size = face.size;
  setFace(ctx, face, size);
  let width = ctx.measureText(text).width;
  if (width > 1) {
    size *= target / width;
    setFace(ctx, face, size);
    width = ctx.measureText(text).width;
  }
  // Never wider than the canvas: the ghost is nowrap and may overhang its own
  // block, and a clipped display letterform is instantly obvious.
  if (width > available && available > 1) {
    setFace(ctx, face, size * (available / width));
  }
}

export interface ChaosFieldProps {
  /** The word knocked out of the field. One word reads best; two is the limit. */
  text: string;
  className?: string;
  /** Set the word in the display face's italic cut. */
  italic?: boolean;
}

export function ChaosField({ text, className, italic = false }: ChaosFieldProps) {
  const wrapRef = useRef<HTMLSpanElement | null>(null);
  const ghostRef = useRef<HTMLSpanElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // Flipped once a frame has actually landed, which is when the ghost can
  // safely go transparent. Until then the word stays legible.
  const [painted, setPainted] = useState(false);

  useEffect(() => {
    const wrap = wrapRef.current;
    const ghost = ghostRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !ghost || !canvas) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    const buffer = document.createElement('canvas');
    const bufferCtx = buffer.getContext('2d', { alpha: false });
    if (!bufferCtx) return;

    const reduced = prefersReducedMotion();

    let frame = 0;
    let disposed = false;
    let onScreen = true;
    let running = false;
    let phase = reduced ? FROZEN_PHASE : 0;
    let announced = false;
    let lastTick = 0;
    let lastDraw = 0;

    /* Layout state, recomputed on resize and after fonts load. */
    let cssWidth = 0;
    let cssHeight = 0;
    let bufferWidth = 0;
    let bufferHeight = 0;
    let image: ImageData | null = null;
    let ready = false;
    let face: FaceSpec = { style: 'normal', family: 'serif', size: 16, trackingEm: 0 };

    function layout(): void {
      if (disposed || !wrap || !ghost || !canvas || !ctx || !bufferCtx) return;
      const box = canvas.getBoundingClientRect();
      cssWidth = Math.max(1, Math.round(box.width));
      cssHeight = Math.max(1, Math.round(box.height));

      const dpr = Math.min(MAX_DPR, window.devicePixelRatio || 1);
      canvas.width = Math.max(1, Math.round(cssWidth * dpr));
      canvas.height = Math.max(1, Math.round(cssHeight * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Noise buffer: same aspect, area capped. Warped fBm is low frequency, so
      // the upscale is free visually and this is what keeps a laptop cool.
      const aspect = cssWidth / cssHeight;
      const rawWidth = Math.sqrt(MAX_FIELD_PIXELS * aspect);
      bufferWidth = Math.max(16, Math.min(cssWidth, Math.round(rawWidth)));
      bufferHeight = Math.max(8, Math.round(bufferWidth / aspect));
      buffer.width = bufferWidth;
      buffer.height = bufferHeight;
      image = bufferCtx.createImageData(bufferWidth, bufferHeight);

      const computed = window.getComputedStyle(ghost);
      const fontSize = parseFloat(computed.fontSize) || 16;
      const spacing = parseFloat(computed.letterSpacing);
      face = {
        family: computed.fontFamily || 'serif',
        style: computed.fontStyle === 'italic' ? 'italic' : 'normal',
        size: fontSize,
        trackingEm: Number.isFinite(spacing) ? spacing / fontSize : 0,
      };
      ready = true;
    }

    function paintField(t: number): void {
      if (!image) return;
      const data = image.data;
      // Noise-space scale: roughly six lattice cells across the word.
      const sx = 5.5 / bufferWidth;
      const sy = (5.5 / bufferWidth) * (bufferWidth / bufferHeight) * 0.42;
      let i = 0;
      for (let y = 0; y < bufferHeight; y += 1) {
        const ny = y * sy;
        for (let x = 0; x < bufferWidth; x += 1) {
          const raw = field(x * sx, ny, t);
          let v = (raw - 0.3) / 0.4;
          v = v < 0 ? 0 : v > 1 ? 1 : v;

          let stop = 1;
          while (stop < RAMP.length - 1 && v > RAMP[stop][0]) stop += 1;
          const lo = RAMP[stop - 1];
          const hi = RAMP[stop];
          const span = hi[0] - lo[0] || 1;
          const k = (v - lo[0]) / span;
          let r = lo[1] + (hi[1] - lo[1]) * k;
          let g = lo[2] + (hi[2] - lo[2]) * k;
          let b = lo[3] + (hi[3] - lo[3]) * k;

          // A printer's registration line: wherever the field crosses one
          // exact value, the flash plate shows through as a thin contour.
          const edge = Math.abs(v - 0.63);
          if (edge < 0.028) {
            const m = 1 - edge / 0.028;
            r += (0xff - r) * m;
            g += (0x3b - g) * m;
            b += (0x00 - b) * m;
          }

          data[i] = r;
          data[i + 1] = g;
          data[i + 2] = b;
          data[i + 3] = 255;
          i += 4;
        }
      }
      bufferCtx!.putImageData(image, 0, 0);
    }

    function draw(t: number): void {
      if (!ready || !ctx || !canvas || !ghost || !wrap) return;
      const metrics = measureGhost(ghost, canvas);
      if (!metrics) return;

      paintField(t);

      ctx.globalCompositeOperation = 'source-over';
      ctx.clearRect(0, 0, cssWidth, cssHeight);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(buffer, 0, 0, bufferWidth, bufferHeight, 0, 0, cssWidth, cssHeight);

      fitFace(ctx, text, face, metrics.width, cssWidth - metrics.left - 1);
      const m = ctx.measureText(text);
      const ascent = m.actualBoundingBoxAscent || 0;
      const descent = m.actualBoundingBoxDescent || 0;
      const baseline = metrics.centerY + (ascent - descent) / 2;

      // Keep only what falls inside the letterforms.
      ctx.globalCompositeOperation = 'destination-in';
      ctx.fillStyle = '#000';
      ctx.fillText(text, metrics.left, baseline);

      // Then drop a mis-registered flash plate in behind it.
      ctx.globalCompositeOperation = 'destination-over';
      ctx.fillStyle = 'rgba(255, 59, 0, 0.55)';
      ctx.fillText(text, metrics.left + 2, baseline + 1);

      ctx.globalCompositeOperation = 'source-over';

      // Once — not on every one of the 24 frames a second.
      if (!announced) {
        announced = true;
        setPainted(true);
      }
    }

    function tick(now: number): void {
      if (disposed) return;
      frame = window.requestAnimationFrame(tick);
      if (lastTick === 0) lastTick = now;
      const delta = Math.min(120, now - lastTick);
      lastTick = now;
      phase += (delta / 1000) * DRIFT_PER_SECOND;
      if (now - lastDraw < FRAME_MS) return;
      lastDraw = now;
      draw(phase);
    }

    function start(): void {
      if (disposed || running || reduced || !onScreen) return;
      running = true;
      lastTick = 0;
      lastDraw = 0;
      frame = window.requestAnimationFrame(tick);
    }

    function stop(): void {
      running = false;
      if (frame) window.cancelAnimationFrame(frame);
      frame = 0;
    }

    function relayout(): void {
      layout();
      draw(phase);
    }

    layout();
    draw(phase);

    // Canvas text needs the display face to actually be loaded, otherwise the
    // first frame is a fallback serif at the wrong width.
    if (typeof document !== 'undefined' && 'fonts' in document) {
      document.fonts.ready.then(() => {
        if (disposed) return;
        relayout();
      }, () => undefined);
    }

    const observer =
      typeof IntersectionObserver === 'undefined'
        ? null
        : new IntersectionObserver(
            (entries) => {
              for (const entry of entries) {
                onScreen = entry.isIntersecting;
                if (onScreen) start();
                else stop();
              }
            },
            { rootMargin: '120px' },
          );
    observer?.observe(wrap);
    if (!observer) start();

    const resize =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => relayout());
    resize?.observe(wrap);

    const onVisibility = (): void => {
      if (document.hidden) stop();
      else start();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      disposed = true;
      stop();
      observer?.disconnect();
      resize?.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [text, italic]);

  return (
    <span
      ref={wrapRef}
      className={cx(s.wrap, italic && s.italic, className)}
      data-painted={painted ? 'true' : 'false'}
    >
      <span ref={ghostRef} className={s.ghost}>
        {text}
      </span>
      <canvas ref={canvasRef} className={s.canvas} aria-hidden="true" />
    </span>
  );
}
